import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi, type LoginPayload, type RegisterPayload } from '@/api';
import router from '@/router';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatarUrl: string | null;
  companyId: string;
  company: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    subscriptionStatus: string;
  };
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value && !!localStorage.getItem('access_token'));
  const isAdmin = computed(() => ['SUPER_ADMIN', 'COMPANY_ADMIN'].includes(user.value?.role ?? ''));
  const canAnalyze = computed(() => ['SUPER_ADMIN', 'COMPANY_ADMIN', 'ANALYST'].includes(user.value?.role ?? ''));

  async function login(payload: LoginPayload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await authApi.login(payload);
      const { tokens, user: userData } = data.data;
      localStorage.setItem('access_token', tokens.accessToken);
      localStorage.setItem('refresh_token', tokens.refreshToken);
      user.value = userData;
      await router.push('/dashboard');
    } catch (err: any) {
      error.value = err.response?.data?.error ?? 'Login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload: RegisterPayload) {
    loading.value = true;
    error.value = null;
    try {
      const { data } = await authApi.register(payload);
      const { tokens, user: userData } = data.data;
      localStorage.setItem('access_token', tokens.accessToken);
      localStorage.setItem('refresh_token', tokens.refreshToken);
      user.value = userData;
      await router.push('/dashboard');
    } catch (err: any) {
      error.value = err.response?.data?.error ?? 'Registration failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => {});
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    user.value = null;
    await router.push('/auth/login');
  }

  async function tryRestoreSession() {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const { data } = await authApi.me();
      user.value = data.data;
    } catch {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    canAnalyze,
    login,
    register,
    logout,
    tryRestoreSession,
  };
});
